import { memo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PageNavBarProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function PageNavBarInner({ currentPage, totalPages, onPageChange }: PageNavBarProps) {
  if (totalPages <= 1) return null;

  const maxDots = 5;
  const showDots = totalPages <= maxDots;

  return (
    <div className="bg-white px-3 py-2 flex items-center justify-between gap-2 shrink-0 border-t border-[#f0d2f0]/50">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className="flex items-center gap-1 px-3.5 py-2 rounded-xl bg-[#f8e8f8] text-[#8e4585] font-medium text-sm disabled:opacity-30 disabled:cursor-not-allowed active:bg-[#f0d2f0] transition-colors min-h-[40px]"
        aria-label="Previous page"
      >
        <ChevronLeft size={16} />
        Prev
      </button>

      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f3f4f6]">
        {showDots && (
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalPages }, (_, i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i + 1 === currentPage ? 'bg-[#8e4585]' : 'bg-[#e5bce5]'
                }`}
              />
            ))}
          </div>
        )}
        <span className="text-[#5a3d58] text-xs font-medium">
          {currentPage} / {totalPages}
        </span>
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className="flex items-center gap-1 px-3.5 py-2 rounded-xl bg-[#8e4585] text-white font-medium text-sm disabled:opacity-30 disabled:cursor-not-allowed active:bg-[#63345e] transition-colors min-h-[40px]"
        aria-label="Next page"
      >
        Next
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

export const PageNavBar = memo(PageNavBarInner);
